'use client';

/**
 * LinearScaleField — Google Forms style linear scale (e.g. 1–5 with labels).
 *
 * Config via field.metadata:
 *   min: number (default 1)
 *   max: number (default 5)
 *   min_label: string
 *   max_label: string
 */

import React from 'react';

import type { FieldComponentField, FieldValue } from '../formFieldTypes';

interface LinearScaleFieldProps {
  field: FieldComponentField;
  value: FieldValue;
  onChange: (value: number | null) => void;
  isDark: boolean;
  readOnly?: boolean;
}

export const LinearScaleField: React.FC<LinearScaleFieldProps> = ({
  field, value, onChange, isDark, readOnly,
}) => {
  const meta = field.metadata as {
    min?: number;
    max?: number;
    min_label?: string;
    max_label?: string;
  } | undefined;
  const validation = field.validation as { min?: number; max?: number } | undefined;
  const min = meta?.min ?? validation?.min ?? 1;
  const max = meta?.max ?? validation?.max ?? 5;
  const minLabel = meta?.min_label || '';
  const maxLabel = meta?.max_label || '';
  const current = typeof value === 'number' ? value : null;
  const count = max - min + 1;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-center gap-1.5 flex-wrap">
        {Array.from({ length: count }, (_, i) => min + i).map((n) => {
          const active = current === n;
          return (
            <button
              key={n}
              type="button"
              disabled={readOnly}
              onClick={() => onChange(active ? null : n)}
              className={`
                w-10 h-10 rounded-full text-sm font-semibold border-2 transition-all
                ${active
                  ? isDark
                    ? 'bg-blue-600 border-blue-500 text-white shadow-lg scale-110'
                    : 'bg-blue-600 border-blue-600 text-white shadow-lg scale-110'
                  : isDark
                    ? 'bg-gray-700 border-gray-600 text-gray-300 hover:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
                }
                ${readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-105'}
              `}
            >
              {n}
            </button>
          );
        })}
      </div>

      {(minLabel || maxLabel) && (
        <div className={`flex justify-between text-xs px-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <span>{minLabel}</span>
          <span>{maxLabel}</span>
        </div>
      )}
    </div>
  );
};
